import {
  getClient,
  getClients,
  getCustomer,
  getCustomization,
  getCustomizations,
  getOrderIdByShopOrderNo,
  getOrdersMissingResult,
  getSettings,
  updateCustomer,
  getUser,
  updateUser
} from "@/modules/firestore_repo";
import {
  getServerDate,
  createCustomer,
  getCustomers,
  getUsersByCustomerId,
  loadCustomer,
  sendOrder,
  createAuthUser
} from "@/modules/functions_repo";
import * as segmentation from "./admin_context_segmentation";

const state = {
  alert: null,

  clientList: null,

  customerList: null,
  customizationList: null,
  customerDetailHead: null,
  customerDetailItems: null,

  loading: false,
  loadingDetailHead: false,
  loadingDetailItems: false,
  sending: false,
  isMaintenance: false,
  maintenanceDateEnd: null,
  orderMissingResultList: null,
  maintenanceMessage: "",
  showMaintenanceWarning: false,

  ...segmentation.state
};

const getters = {
  getCustomerDetailHead: state => {
    return state.customerDetailHead;
  },
  getCustomerDetailItems: state => {
    return state.customerDetailItems || [];
  },
  getClientList: state => {
    return state.clientList || [];
  },
  getCustomerList: state => {
    return state.customerList || [];
  },
  getCustomizationList: state => {
    return state.customizationList || [];
  },
  getIsMaintenance: state => {
    return state.isMaintenance;
  },
  getMaintenanceDateEnd: state => {
    return state.maintenanceDateEnd;
  },
  getMaintenanceMessage: state => {
    return state.maintenanceMessage;
  },
  getShowMaintenanceWarning: state => {
    return state.showMaintenanceWarning;
  },
  getOrderMissingResultList: state => {
    return state.orderMissingResultList || [];
  },

  ...segmentation.getters
};

const actions = {
  fetchIsMaintenance: async ({ commit, rootGetters }) => {
    const data = await getSettings("_default");
    const serverDate = await getServerDate();
    const userIsAdmin = rootGetters["user/isAdmin"];

    // maintenanceMessage
    // (string)
    commit("setMaintenanceMessage", data.maintenanceMessage);

    // showMaintenanceWarning
    // (boolean)
    commit("setShowMaintenanceWarning", data.showMaintenanceWarning);

    if (userIsAdmin && data.maintenancePermitAdminUser) return;

    if (data.maintenanceDateStart) {
      if (data.maintenanceDateStart.toMillis() < serverDate) {
        commit("setIsMaintenance", true);
      }
      if (data.maintenanceDateEnd) {
        commit("setMaintenanceDateEnd", data.maintenanceDateEnd.toDate());
        if (data.maintenanceDateEnd.toMillis() < serverDate) {
          commit("setIsMaintenance", false);
        }
      }
    }
  },
  fetchClientList: async ({ commit }) => {
    const data = await getClients();
    commit("setClientList", data);
  },
  fetchCustomerList: async ({ commit, dispatch }) => {
    commit("setLoading", true);
    const customers = await getCustomers();
    const clients = await getClients();
    const customizations = await getCustomizations();
    console.assert(customers instanceof Array, "customers must be an array");
    console.assert(clients instanceof Array, "clients must be an array");
    console.assert(
      customizations instanceof Array,
      "customizations must be an array"
    );
    for (let i = 0; i < customers.length; i++) {
      const client = clients.find(x => x.id === customers[i].clientId);
      customers[i].divisionLabel = client.divisionLabel || client.division;
      customers[i].clientLabel = client.label || client.id;
      customers[i].productHierarchyString =
        customers[i].productHierarchies instanceof Array
          ? customers[i].productHierarchies.join(";")
          : "";
    }
    for (let i = 0; i < customers.length; i++) {
      const customizationLabel = customizations.find(
        x => x.id === customers[i].customization
      ).label;
      customers[i].customizationLabel =
        customizationLabel || customers[i].customization;
    }
    commit("setCustomerList", customers);
    dispatch("fetchClientList");
    dispatch("fetchCustomizationList");
    commit("setLoading", false);
  },
  fetchCustomerDetailHead: async ({ commit }, { customerId }) => {
    console.assert(customerId, "customerId must not be falsy");
    commit("setLoadingDetailHead", true);
    try {
      const customer = await getCustomer(customerId);
      const { label: customizationLabel } = await getCustomization(customerId);
      const client = await getClient(customer.clientId);
      customer.divisionLabel = client.divisionLabel;
      customer.clientLabel = client.label;
      customer.customizationLabel = customizationLabel;

      commit("setCustomerDetailHead", customer);
    } catch (error) {
      commit("setAlert", { message: error.message || error, type: "error" });
    }
    commit("setLoadingDetailHead", false);
  },
  fetchCustomerDetailItems: async ({ commit }, { customerId }) => {
    console.assert(customerId, "customerId must not be falsy");
    commit("setLoadingDetailItems", true);
    try {
      const data = await getUsersByCustomerId(customerId);
      commit("setCustomerDetailItems", data);
    } catch (error) {
      commit("setAlert", { message: error.message || error, type: "error" });
    }
    commit("setLoadingDetailItems", false);
  },
  fetchCustomizationList: async ({ commit }) => {
    const data = await getCustomizations();
    commit("setCustomizationList", data);
  },
  fetchOrderMissingResultList: async ({ commit }) => {
    const data = await getOrdersMissingResult();
    commit("setOrderMissingResultList", data);
  },
  resendOrder: async ({ commit }, { shopOrderNo }) => {
    commit("setSending", true);
    try {
      const orderId = await getOrderIdByShopOrderNo(shopOrderNo);
      if (!orderId) {
        throw `Shop Order No. ${shopOrderNo} not found.`;
      }
      const orderResultId = await sendOrder(orderId, true);
      if (orderResultId) {
        commit("setAlert", {
          message: `Shop Order No. ${shopOrderNo} was resent.`,
          type: "success"
        });
      }
    } catch (error) {
      commit("setAlert", {
        message: error.message || error,
        type: "error"
      });
    }
    commit("setSending", false);
  },
  stressOrder: async ({ commit }, { orderId }) => {
    commit("setSending", true);
    try {
      const orderResultId = await sendOrder(orderId, false);
      if (orderResultId) {
        commit("setAlert", {
          message: `Order ID ${orderId} was resent.`,
          type: "success"
        });
        return orderResultId;
      }
    } catch (error) {
      commit("setAlert", {
        message: error.message || error,
        type: "error"
      });
      throw error;
    } finally {
      commit("setSending", false);
    }
  },
  updateCustomer: async ({ dispatch, commit }, { id, data }) => {
    commit("setSending", true);
    try {
      await updateCustomer(id, data);
      commit("setAlert", {
        message: "Customer has been updated.",
        type: "success"
      });
    } catch (error) {
      commit("setAlert", {
        message: error.message || error,
        type: "error"
      });
      throw error;
    } finally {
      commit("setSending", false);
      dispatch("fetchCustomerList");
    }
  },
  addCustomer: async ({ dispatch, commit }, { data }) => {
    commit("setSending", true);
    try {
      const customerId = await createCustomer(data.number, data);
      if (customerId) {
        await loadCustomer(customerId);
        commit("setAlert", {
          message: "Customer has been added.",
          type: "success"
        });
        return customerId;
      }
      commit("setAlert", {
        message: "An error occured while creating the customer",
        type: "error"
      });
      return;
    } catch (error) {
      commit("setAlert", {
        message: error.message || error,
        type: "error"
      });
      return;
    } finally {
      commit("setSending", false);
      dispatch("fetchCustomerList");
    }
  },
  removeCustomerDetailItem: async (
    { dispatch, commit },
    { customerId, itemId }
  ) => {
    console.assert(customerId, "customerId must not be falsy");
    console.assert(itemId, "itemId must not be falsy");
    commit("setLoadingDetailItems", true);
    try {
      const { customerIds, ...user } = await getUser(itemId);
      if (user) {
        const position = customerIds.indexOf(customerId);
        if (~position) customerIds.splice(position, 1);
        await updateUser(itemId, { customerIds, activeCustomer: null });
        commit("setAlert", {
          message: "User has been removed.",
          type: "success"
        });
      }
    } catch (error) {
      commit("setAlert", { message: error.message || error, type: "error" });
    }
    commit("setLoadingDetailItems", false);
    dispatch("fetchCustomerDetailItems", { customerId });
  },
  addCustomerDetailItem: async ({ dispatch, commit }, { customerId, data }) => {
    console.assert(customerId, "customerId must not be falsy");
    console.assert(data, "data must not be falsy");
    commit("setLoadingDetailItems", true);
    try {
      const userId = await createAuthUser(data.email);
      if (userId) {
        const { customerIds } = await getUser(userId);
        const customerIdList = customerIds || [];
        if (customerIdList.indexOf(customerId) == -1) {
          customerIdList.push(customerId);
          await updateUser(userId, { customerIds: customerIdList });
        }
      } else {
        commit("setAlert", { message: "Creating user faild", type: "error" });
      }
      commit("setAlert", {
        message: "User has been added.",
        type: "success"
      });
    } catch (error) {
      commit("setAlert", { message: error.message || error, type: "error" });
    }
    commit("setLoadingDetailItems", false);
    dispatch("fetchCustomerDetailItems", { customerId });
  },

  ...segmentation.actions
};

const mutations = {
  setIsMaintenance: (state, payload) => {
    state.isMaintenance = payload;
  },
  setMaintenanceDateEnd: (state, payload) => {
    state.maintenanceDateEnd = payload;
  },
  setMaintenanceMessage: (state, payload) => {
    state.maintenanceMessage = payload;
  },
  setShowMaintenanceWarning: (state, payload) => {
    state.showMaintenanceWarning = payload;
  },
  setAlert: (state, payload) => {
    state.alert = payload;
  },
  setClientList: (state, payload) => {
    state.clientList = payload.map(client => {
      return {
        id: client.id,
        label: client.label || client.id
      };
    });
  },
  setCustomerList: (state, payload) => {
    state.customerList = payload.map(customer => {
      return {
        canAddShipTo: customer.canAddShipTo || false,
        city: customer.city,
        clientId: customer.clientId,
        clientLabel: customer.clientLabel,
        divisionLabel: customer.divisionLabel,
        country: customer.country,
        customization: customer.customization,
        customizationLabel: customer.customizationLabel,
        id: customer.id,
        name1: customer.name1,
        name2: customer.name2,
        name3: customer.name3,
        number: customer.number,
        postalCode: customer.postalCode,
        street: customer.street,
        houseNumber: customer.houseNumber,
        productHierarchyString: customer.productHierarchyString,
        isOptionalOemEnabled: customer.isOptionalOemEnabled || false
      };
    });
  },
  setCustomizationList: (state, payload) => {
    state.customizationList = payload.map(customization => {
      return {
        id: customization.id,
        label: customization.label || customization.id
      };
    });
  },
  setLoading: (state, payload) => {
    state.loading = payload;
  },
  setLoadingDetailHead: (state, payload) => {
    state.loadingDetailHead = payload;
  },
  setLoadingDetailItems: (state, payload) => {
    state.loadingDetailItems = payload;
  },
  setSending: (state, payload) => {
    state.sending = payload;
  },
  setCustomerDetailHead: (state, data) => {
    state.customerDetailHead = data;
  },
  setCustomerDetailItems: (state, data) => {
    state.customerDetailItems = data;
  },
  setOrderMissingResultList: (state, payload) => {
    state.orderMissingResultList = payload.map(order => {
      return {
        id: order.id,
        shopOrderNo: order.shopOrderNo,
        customersOrderNo: order.customersOrderNo,
        timestamp: order.timestamp
      };
    });
  },

  ...segmentation.mutations
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state
};
